import { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { ManagerCard } from "./manager-card";
import { AddManagerSheet } from "./add-manager-sheet";
import { authClient } from "@/lib/auth-client";
import { Skeleton } from "@/components/ui/skeleton";

// Maximum number of managers allowed per organization
const MAX_MANAGERS = 5;

// Types for members and invitations from better-auth
interface Member {
  id: string;
  role: string;
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
  };
}

interface Invitation {
  id: string;
  email: string;
  role: string | null;
  status: string;
}

export function ManagersSection() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [managers, setManagers] = useState<Member[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch members and invitations
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      // Fetch organization members
      const membersResult = await authClient.organization.listMembers();
      if (membersResult.data) {
        // Filter to only owners and admins (managers)
        const filteredMembers = membersResult.data.members.filter(
          (member: Member) => member.role === "owner" || member.role === "admin"
        );
        setManagers(filteredMembers);
      }

      // Fetch pending invitations
      const invitationsResult = await authClient.organization.listInvitations();
      if (invitationsResult.data) {
        // Filter to pending admin invitations
        const filteredInvitations = invitationsResult.data.filter(
          (inv: Invitation) => inv.status === "pending" && inv.role === "admin"
        );
        setPendingInvitations(filteredInvitations);
      }
    } catch (error) {
      console.error("Error fetching managers:", error);
      toast.error("Erreur lors du chargement des gérants");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get existing emails (both members and pending invitations)
  const existingEmails = [
    ...managers.map((m) => m.user.email.toLowerCase()),
    ...pendingInvitations.map((inv) => inv.email.toLowerCase()),
  ];

  const totalManagersCount = managers.length + pendingInvitations.length;

  const handleAddClick = () => {
    if (totalManagersCount >= MAX_MANAGERS) {
      toast.error("Limite atteinte", {
        description: `Vous ne pouvez pas ajouter plus de ${MAX_MANAGERS} gérants.`,
      });
      return;
    }
    setIsSheetOpen(true);
  };

  const handleAddSuccess = () => {
    fetchData();
  };

  const handleDeleteManager = async (memberId: string) => {
    try {
      const result = await authClient.organization.removeMember({
        memberIdOrEmail: memberId,
      });

      if (result.error) {
        toast.error("Erreur lors de la suppression", {
          description: result.error.message,
        });
        return;
      }

      toast.success("Gérant supprimé");
      fetchData();
    } catch {
      toast.error("Erreur lors de la suppression du gérant");
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const result = await authClient.organization.cancelInvitation({
        invitationId,
      });

      if (result.error) {
        toast.error("Erreur lors de l'annulation", {
          description: result.error.message,
        });
        return;
      }

      toast.success("Invitation annulée");
      fetchData();
    } catch {
      toast.error("Erreur lors de l'annulation de l'invitation");
    }
  };

  if (isLoading) {
    return (
      <section>
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Gérants</h2>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-64 w-64 shrink-0 rounded-lg" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-gray-900">Gérants</h2>

      <div className="flex gap-4 overflow-x-auto pb-2">
        {/* Active manager cards */}
        {managers.map((member) => (
          <ManagerCard
            key={member.id}
            manager={{
              id: member.id,
              firstName: member.user.name.split(" ")[0] || "",
              lastName: member.user.name.split(" ").slice(1).join(" ") || "",
              email: member.user.email,
              phone: "", // Phone not stored in user table
              role: member.role as "owner" | "admin",
            }}
            onDelete={handleDeleteManager}
            canDelete={managers.length > 1}
          />
        ))}

        {/* Pending invitation cards */}
        {pendingInvitations.map((invitation) => (
          <ManagerCard
            key={invitation.id}
            manager={{
              id: invitation.id,
              firstName: invitation.email.split("@")[0] || "",
              lastName: "",
              email: invitation.email,
              phone: "",
              role: "admin",
            }}
            onDelete={handleCancelInvitation}
            canDelete={true}
            isPending={true}
          />
        ))}

        {/* Add manager button */}
        {totalManagersCount < MAX_MANAGERS && (
          <button
            onClick={handleAddClick}
            className="flex h-auto w-64 shrink-0 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 p-4 text-gray-500 transition-colors hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600 hover:cursor-pointer"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
              <Plus className="h-5 w-5 text-orange-500" />
            </div>
            <span className="text-sm font-medium">Ajouter un gérant</span>
          </button>
        )}
      </div>

      {/* Add manager sheet */}
      <AddManagerSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onSuccess={handleAddSuccess}
        existingEmails={existingEmails}
        managersCount={totalManagersCount}
      />
    </section>
  );
}
